import React, { useState, useEffect, useRef } from "react";
import { decodeToken } from '../utils/dataUtils';
import { toast } from 'react-toastify';
import api from "../services/api";

const Login = () => {
  const [step, setStep] = useState("login");
  const [email, setEmail] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(60);

  const inputRefs = useRef([]);

  // Mask email like s***@gmail.com
  const maskEmail = (email) => {
    const [name, domain] = email.split("@");
    return name[0] + "***@" + domain;
  };

  // ⏱️ Timer logic
  useEffect(() => {
    if (step === "otp" && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [step, timer]);

  // 🔐 LOGIN
  const handleLogin = async () => {
    try {
      setLoading(true);

      const res = await api.post("/auth/login", {
        email,
        password,
      });

      setMaskedEmail(maskEmail(email));
      setStep("otp");
      setTimer(60);

      toast.success("OTP sent to your email");

      // Focus first OTP input
      setTimeout(() => inputRefs.current[0]?.focus(), 100);

    } catch (err) {
      if (err.response?.status === 429) {
        toast.error("Too many attempts. Try again later.");
      } else {
        toast.error(err.response?.data?.message || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  // 🔢 OTP INPUT HANDLER
  const handleOtpChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move forward
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  // ⬅️ BACKSPACE HANDLING
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  // 🔍 VERIFY OTP
  const handleVerifyOtp = async () => {
    try {
      setLoading(true);

      const finalOtp = otp.join("");

      if (finalOtp.length !== 6) {
        return toast.error("Enter complete OTP");
      }

      const res = await api.post("/auth/verify-otp", {
      // const res = await axios.post("https://erp-server-3qrl.onrender.com/api/auth/verify-otp", {
        email,
        otp: finalOtp,
      });

      localStorage.setItem("token", res.data.token);
        const token = localStorage.getItem('token');

        if (token) {
            const userData = decodeToken(token);
            // console.log('Decoded User Data:', userData);
            localStorage.setItem('userId', userData.userId); // Store user name separately
            localStorage.setItem('userCompany', userData.company); // Store user name separately
            localStorage.setItem('userRole', userData.role); // Store user name separately
            localStorage.setItem('userName', userData.fullname); // Store user name separately
            localStorage.setItem('sessionId', userData.sessionId); // Save token to localStorage
        }


      toast.success("Login successful 🎉");

      // Redirect
      window.location.href = "/dashboard";

    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  // 🔁 RESEND OTP
  const handleResendOtp = async () => {
    try {
      const res = await api.post("/auth/resend-otp", { email });
      // const res = await axios.post("https://erp-server-3qrl.onrender.com/api/auth/resend-otp", { email });

      toast.success("OTP resent");
      setTimer(60);

    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to resend OTP");
    }
  };

  return (
    <div style={styles.container}>

      <div style={styles.card}>
        <h2 style={styles.title}>
          {step === "login" ? "Login" : "Verify OTP"}
        </h2>

        {step === "login" && (
          <>
            <input
              style={styles.input}
              type="email"
              placeholder="Email"
              onChange={(e) => setEmail(e.target.value)}
            />

            <input
              style={styles.input}
              type="password"
              placeholder="Password"
              onChange={(e) => setPassword(e.target.value)}
            />

            <button
              style={styles.button}
              onClick={handleLogin}
              disabled={loading}
            >
              {loading ? "Sending OTP..." : "Login"}
            </button>
          </>
        )}

        {step === "otp" && (
          <>
            <p style={styles.subtitle}>
              Enter OTP sent to <b>{maskedEmail}</b>
            </p>

            <div style={styles.otpContainer}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  style={styles.otpInput}
                  maxLength={1}
                  value={digit}
                  onChange={(e) =>
                    handleOtpChange(e.target.value, index)
                  }
                  onKeyDown={(e) => handleKeyDown(e, index)}
                />
              ))}
            </div>

            <button
              style={styles.button}
              onClick={handleVerifyOtp}
              disabled={loading}
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>

            <button
              style={{
                ...styles.resendBtn,
                opacity: timer > 0 ? 0.5 : 1,
              }}
              onClick={handleResendOtp}
              disabled={timer > 0}
            >
              {timer > 0
                ? `Resend in ${timer}s`
                : "Resend OTP"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;

// 🎨 Basic styling (you can replace with Tailwind later)
const styles = {
  container: {
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "#f5f6fa",
  },
  card: {
    padding: "30px",
    borderRadius: "10px",
    background: "#fff",
    width: "320px",
    boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
    textAlign: "center",
  },
  title: {
    marginBottom: "20px",
  },
  subtitle: {
    fontSize: "14px",
    marginBottom: "15px",
  },
  input: {
    width: "100%",
    padding: "10px",
    marginBottom: "10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
  },
  button: {
    width: "100%",
    padding: "10px",
    background: "#4f46e5",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  otpContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "15px",
  },
  otpInput: {
    width: "40px",
    height: "40px",
    textAlign: "center",
    fontSize: "18px",
    borderRadius: "6px",
    border: "1px solid #ccc",
  },
  resendBtn: {
    marginTop: "10px",
    background: "none",
    border: "none",
    color: "#4f46e5",
    cursor: "pointer",
  },
};